import {
  buffers, eventChannel, type TakeableChannel,
} from 'redux-saga';
import {
  actionChannel,
  call,
  delay, put,
  race, take, takeLatest,
} from 'redux-saga/effects';
import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import {
  registerStatsEventsBroadcastListener, type StatsEvent,
} from '~/_shared/utils/api/broadcast.helpers';
import { flatten } from '~/_shared/utils/collections/collections';
import { noop } from '~/_shared/utils/function.helpers';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import { select } from '~/_shared/utils/saga/effects';
import { notNull } from '~/_shared/utils/typeGuards';
import { clientIdSelector } from '../selectors/useClientIdSelector';
import { userDataIsReadySelector } from '../userData/settings/userDataSettings.selectors';
import {
  USER_DATA_SETTING_READY_SET, USER_GET_DATA_SUCCESS,
  USER_WEB_SOCKET_BROADCAST_INITIALIZED,
} from '../userData/userData.actionTypes';
import {
  addBoundaryStatsItems, requestBoundaryStatsItemsByIdIfExist, updateBoundaryStatsItems,
} from './stats.actionCreators';
import { createLoadedStatItem } from './stats.factory';
import { boundaryStatsItemToBoundaryStatsRequestItem } from './stats.helpers';
import {
  type BoundaryStatsRequestItem,
  type BoundaryStatsResponse, getBoundaryStats,
} from './stats.repository';
import { boundaryGroupStatsSelector } from './stats.selectors';

export function* statsSagas() {
  yield takeLatest(USER_GET_DATA_SUCCESS, watchForLoadStats);
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, registerStatsSocketEventsProcessor);
}

/*
 * When the status of any item is changed to FetchableResourceStatus.Requested,
 * this saga should handle loading if from the backend
*/
function* watchForLoadStats() {
  // Buffer latest map settings action when the saga is busy and not taking actions.
  const statsChannel: TakeableChannel<Action> = yield actionChannel(
    [addBoundaryStatsItems.type, updateBoundaryStatsItems.type], buffers.sliding(1));

  while (true) {
    let isUserDataSettingsReady: boolean = yield select(userDataIsReadySelector);

    while (!isUserDataSettingsReady) {
      yield take(USER_DATA_SETTING_READY_SET);
      isUserDataSettingsReady = yield select(userDataIsReadySelector);
    }

    const clientId: number | null = yield select(clientIdSelector);

    if (!clientId) {
      continue;
    }

    yield take(statsChannel);

    while (true) {

      const { debounce } = yield race({
        debounce: delay(1000),
        latestAction: take(statsChannel),
      });

      if (debounce) {
        break;
      }
    }

    const boundaryGroupsStats: ReturnType<typeof boundaryGroupStatsSelector> = yield select(boundaryGroupStatsSelector);

    const statsPerBoundary = Array.from(boundaryGroupsStats.values());
    const allStats = flatten(
      statsPerBoundary.map((stats) => Array.from(stats.values()))
    );
    const statsToFetch = allStats.filter((stat) => (
      stat.data.status === FetchableResourceStatus.Requested
    ));

    if (statsToFetch.length === 0) {
      continue;
    }

    yield put(updateBoundaryStatsItems(statsToFetch.map((stat) => ({
      ...stat,
      data: {
        status: FetchableResourceStatus.Loading,
      },
    }))));

    const queryItems: ReadonlyArray<BoundaryStatsRequestItem> = statsToFetch.map(
      boundaryStatsItemToBoundaryStatsRequestItem
    );

    try {
      const response: BoundaryStatsResponse = yield call(getBoundaryStats, clientId, queryItems);

      const itemsToUpdate = stringObjectEntries(response.result).map(([queryId, queryItem]) => {
        const existingStat = allStats.find((stat) => stat.id === queryId);

        if (existingStat) {
          if (queryItem) {
            return createLoadedStatItem(queryId, queryItem);
          }
          else {
            return {
              ...existingStat,
              data: {
                status: FetchableResourceStatus.NotFound,
              },
            };
          }
        }

        return null;
      }).filter(notNull);

      if (itemsToUpdate.length > 0) {
        yield put(updateBoundaryStatsItems(itemsToUpdate));
      }
    }
    catch (e) {
      // set all requested stats to error state
      const itemsToUpdate = statsToFetch.map((stat) => ({
        ...stat,
        data: {
          status: FetchableResourceStatus.NotFound,
        },
      }));
      if (itemsToUpdate.length > 0) {
        yield put(updateBoundaryStatsItems(itemsToUpdate));
      }
    }
  }
}

function* registerStatsSocketEventsProcessor() {
  const statsChannel: ReturnType<typeof registerStatsListeners> = yield call(registerStatsListeners);

  while (true) {
    const receivedIds = new Set<number>();

    const firstEvent: StatsEvent = yield take(statsChannel);
    if (firstEvent.id) {
      receivedIds.add(firstEvent.id);
    }

    while (true) {
      const { event, debounced } = yield race({
        event: take(statsChannel),
        debounced: delay(5000), // fetching recalculated stats is not time-sensitive, better catch more to stagger the data queries
      });

      if (event) {
        if (event.id) {
          receivedIds.add(event.id);
        }
      }

      if (debounced) {
        break;
      }
    }

    yield put(requestBoundaryStatsItemsByIdIfExist(Array.from(receivedIds)));

  }
}

const registerStatsListeners = () => {
  return eventChannel<StatsEvent>(emitter => {
    registerStatsEventsBroadcastListener({
      onStarted: noop, // for now if the user has already loaded stats the can keep showing the loaded ones until the next load
      onFinished: (e) => {
        emitter(e);
      },
      onError: noop,
    });

    return noop;
  }, buffers.expanding());
};
